import React, { useMemo, useState } from 'react';
import {
	Badge,
	Box,
	Button,
	Checkbox,
	Divider,
	Drawer,
	FormControlLabel,
	FormGroup,
	IconButton,
	InputAdornment,
	MenuItem,
	Stack,
	TextField,
	ToggleButton,
	ToggleButtonGroup,
	Typography,
	alpha,
	useTheme,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import FilterListIcon from '@mui/icons-material/FilterList';
import SearchIcon from '@mui/icons-material/Search';
import { BID_STATUS_COLORS, BID_STATUS_LABELS } from '../../../Shared/bids';
import { surfaceCard } from '../../../Shared/surfaceCard';

const PIPELINE_OPTIONS = [
	{ value: 'all', label: 'All' },
	{ value: 'active', label: 'Active' },
	{ value: 'attention', label: 'Waiting' },
	{ value: 'closed', label: 'Closed' },
];

export const JOB_SORT_OPTIONS = [
	{ value: 'created_desc', label: 'Newest created' },
	{ value: 'created_asc', label: 'Oldest created' },
	{ value: 'due_asc', label: 'Due soonest' },
	{ value: 'due_desc', label: 'Due latest' },
];

const BID_STATUS_OPTIONS = Object.entries(BID_STATUS_LABELS).map(
	([value, label]) => ({
		value,
		label,
		color: BID_STATUS_COLORS[value] || 'default',
	}),
);

const StatusChecklist = ({
	title,
	options,
	selected,
	onChange,
	emptyLabel,
}) => (
	<Box>
		<Typography
			variant="overline"
			color="text.secondary"
			sx={{ display: 'block', mb: 0.5, letterSpacing: 0.6 }}
		>
			{title}
		</Typography>
		{!options.length ? (
			<Typography variant="body2" color="text.secondary">
				{emptyLabel || 'No options available.'}
			</Typography>
		) : (
			<FormGroup sx={{ gap: 0.25 }}>
				{options.map((option) => {
					const checked = selected.includes(option.value);
					return (
						<FormControlLabel
							key={option.value}
							control={
								<Checkbox
									size="small"
									checked={checked}
									onChange={() => {
										onChange(
											checked
												? selected.filter((v) => v !== option.value)
												: [...selected, option.value],
										);
									}}
								/>
							}
							label={
								<Typography
									variant="body2"
									sx={{ fontWeight: checked ? 600 : 400 }}
								>
									{option.label}
								</Typography>
							}
							sx={{
								m: 0,
								mr: 0,
								px: 0.5,
								borderRadius: 1.5,
								bgcolor: checked
									? (t) => alpha(t.palette.primary.main, 0.06)
									: 'transparent',
							}}
						/>
					);
				})}
			</FormGroup>
		)}
	</Box>
);

const JobsMobileFilters = ({
	search,
	onSearchChange,
	pipeline,
	onPipelineChange,
	sort,
	onSortChange,
	bidStatuses,
	onBidStatusesChange,
	jobStatuses,
	onJobStatusesChange,
	customers,
	onCustomersChange,
	customerOptions = [],
	jobStatusOptions = [],
	showJobStatus = false,
	resultCount,
	totalCount,
	onClear,
}) => {
	const theme = useTheme();
	const [sheetOpen, setSheetOpen] = useState(false);
	const [draftBidStatuses, setDraftBidStatuses] = useState(bidStatuses);
	const [draftJobStatuses, setDraftJobStatuses] = useState(jobStatuses);
	const [draftCustomers, setDraftCustomers] = useState(customers);
	const [draftSort, setDraftSort] = useState(sort);

	const advancedCount =
		bidStatuses.length + jobStatuses.length + customers.length;
	const isFiltered =
		Boolean(search.trim()) ||
		pipeline !== 'all' ||
		sort !== 'created_desc' ||
		advancedCount > 0;

	const summaryParts = useMemo(() => {
		const parts = [];
		if (pipeline !== 'all') {
			const label =
				PIPELINE_OPTIONS.find((o) => o.value === pipeline)?.label || pipeline;
			parts.push(label);
		}
		if (customers.length === 1) {
			const label =
				customerOptions.find((o) => o.value === customers[0])?.label ||
				'1 customer';
			parts.push(label);
		} else if (customers.length > 1) {
			parts.push(`${customers.length} customers`);
		}
		if (bidStatuses.length === 1) {
			parts.push(BID_STATUS_LABELS[bidStatuses[0]] || bidStatuses[0]);
		} else if (bidStatuses.length > 1) {
			parts.push(`${bidStatuses.length} bid statuses`);
		}
		if (jobStatuses.length === 1) {
			const label =
				jobStatusOptions.find((o) => o.value === jobStatuses[0])?.label ||
				jobStatuses[0];
			parts.push(label);
		} else if (jobStatuses.length > 1) {
			parts.push(`${jobStatuses.length} job statuses`);
		}
		if (sort !== 'created_desc') {
			const label =
				JOB_SORT_OPTIONS.find((o) => o.value === sort)?.label || 'Custom sort';
			parts.push(label);
		}
		return parts;
	}, [
		pipeline,
		customers,
		customerOptions,
		bidStatuses,
		jobStatuses,
		jobStatusOptions,
		sort,
	]);

	const openSheet = () => {
		setDraftBidStatuses(bidStatuses);
		setDraftJobStatuses(jobStatuses);
		setDraftCustomers(customers);
		setDraftSort(sort);
		setSheetOpen(true);
	};

	const applySheet = () => {
		onBidStatusesChange(draftBidStatuses);
		onJobStatusesChange(draftJobStatuses);
		onCustomersChange(draftCustomers);
		onSortChange(draftSort);
		setSheetOpen(false);
	};

	const clearSheet = () => {
		setDraftBidStatuses([]);
		setDraftJobStatuses([]);
		setDraftCustomers([]);
		setDraftSort('created_desc');
	};

	return (
		<>
			<Stack spacing={1.5}>
				<Stack direction="row" spacing={1} alignItems="center">
					<TextField
						size="small"
						fullWidth
						value={search}
						onChange={(e) => onSearchChange(e.target.value)}
						placeholder="Search jobs or customers"
						InputProps={{
							startAdornment: (
								<InputAdornment position="start">
									<SearchIcon
										fontSize="small"
										sx={{ color: 'text.disabled' }}
									/>
								</InputAdornment>
							),
						}}
						sx={{
							'& .MuiOutlinedInput-root': {
								...surfaceCard(theme),
								borderRadius: 2.5,
								'& fieldset': { border: 'none' },
							},
						}}
					/>
					<IconButton
						aria-label="More filters"
						onClick={openSheet}
						sx={{
							...surfaceCard(theme),
							borderRadius: 2.5,
							width: 42,
							height: 42,
							flexShrink: 0,
							...(advancedCount || sort !== 'created_desc'
								? {
										borderColor: alpha(theme.palette.primary.main, 0.35),
										color: 'primary.main',
									}
								: null),
						}}
					>
						<Badge
							color="primary"
							badgeContent={
								advancedCount + (sort !== 'created_desc' ? 1 : 0) || null
							}
							overlap="circular"
						>
							<FilterListIcon fontSize="small" />
						</Badge>
					</IconButton>
				</Stack>

				<ToggleButtonGroup
					exclusive
					fullWidth
					size="small"
					value={pipeline}
					onChange={(_, next) => {
						if (next) onPipelineChange(next);
					}}
					sx={{
						...surfaceCard(theme),
						borderRadius: 2.5,
						p: 0.4,
						gap: 0.35,
						'& .MuiToggleButtonGroup-grouped': {
							border: 0,
							borderRadius: '10px !important',
							margin: 0,
						},
						'& .MuiToggleButton-root': {
							flex: 1,
							py: 0.85,
							px: 0.5,
							textTransform: 'none',
							fontWeight: 600,
							fontSize: '0.8125rem',
							color: 'text.secondary',
							'&.Mui-selected': {
								bgcolor: (t) =>
									t.palette.mode === 'dark'
										? alpha(t.palette.common.white, 0.1)
										: alpha(t.palette.common.black, 0.06),
								color: 'text.primary',
								'&:hover': {
									bgcolor: (t) =>
										t.palette.mode === 'dark'
											? alpha(t.palette.common.white, 0.14)
											: alpha(t.palette.common.black, 0.08),
								},
							},
						},
					}}
				>
					{PIPELINE_OPTIONS.map((option) => (
						<ToggleButton key={option.value} value={option.value}>
							{option.label}
						</ToggleButton>
					))}
				</ToggleButtonGroup>

				<Stack
					direction="row"
					alignItems="center"
					justifyContent="space-between"
					spacing={1}
					sx={{ minHeight: 20 }}
				>
					<Typography variant="caption" color="text.secondary" noWrap>
						{isFiltered
							? `${resultCount} of ${totalCount}${
									summaryParts.length
										? ` \u00b7 ${summaryParts.join(' \u00b7 ')}`
										: ''
								}`
							: `${totalCount} job${totalCount === 1 ? '' : 's'}`}
					</Typography>
					{isFiltered ? (
						<Button
							size="small"
							onClick={onClear}
							sx={{
								minWidth: 0,
								px: 0.75,
								py: 0,
								textTransform: 'none',
								fontWeight: 600,
								fontSize: '0.75rem',
							}}
						>
							Reset
						</Button>
					) : null}
				</Stack>
			</Stack>

			<Drawer
				anchor="bottom"
				open={sheetOpen}
				onClose={() => setSheetOpen(false)}
				PaperProps={{
					sx: {
						borderTopLeftRadius: 16,
						borderTopRightRadius: 16,
						maxHeight: '85vh',
						pb: 'env(safe-area-inset-bottom)',
					},
				}}
			>
				<Box sx={{ px: 2.5, pt: 2, pb: 2.5 }}>
					<Stack
						direction="row"
						alignItems="center"
						justifyContent="space-between"
						sx={{ mb: 1.5 }}
					>
						<Box>
							<Typography
								variant="h6"
								fontWeight={700}
								sx={{ lineHeight: 1.2 }}
							>
								Filters & sort
							</Typography>
							<Typography variant="caption" color="text.secondary">
								Customer, status, and date order
							</Typography>
						</Box>
						<IconButton
							aria-label="Close filters"
							onClick={() => setSheetOpen(false)}
							edge="end"
						>
							<CloseIcon />
						</IconButton>
					</Stack>

					<Stack
						spacing={2.5}
						sx={{ maxHeight: '55vh', overflowY: 'auto', pb: 1 }}
					>
						<Box>
							<Typography
								variant="overline"
								color="text.secondary"
								sx={{ display: 'block', mb: 0.75, letterSpacing: 0.6 }}
							>
								Sort by
							</Typography>
							<TextField
								select
								size="small"
								fullWidth
								value={draftSort}
								onChange={(e) => setDraftSort(e.target.value)}
								sx={{
									'& .MuiOutlinedInput-root': {
										borderRadius: 2,
									},
								}}
							>
								{JOB_SORT_OPTIONS.map((option) => (
									<MenuItem key={option.value} value={option.value}>
										{option.label}
									</MenuItem>
								))}
							</TextField>
						</Box>

						<Divider />

						<StatusChecklist
							title="Customer"
							options={customerOptions}
							selected={draftCustomers}
							onChange={setDraftCustomers}
							emptyLabel="No customers yet."
						/>

						<Divider />

						<StatusChecklist
							title="Bid status"
							options={BID_STATUS_OPTIONS}
							selected={draftBidStatuses}
							onChange={setDraftBidStatuses}
						/>

						{showJobStatus && jobStatusOptions.length ? (
							<>
								<Divider />
								<StatusChecklist
									title="Job status"
									options={jobStatusOptions}
									selected={draftJobStatuses}
									onChange={setDraftJobStatuses}
								/>
							</>
						) : null}
					</Stack>

					<Stack direction="row" spacing={1.25} sx={{ mt: 2 }}>
						<Button
							fullWidth
							variant="outlined"
							onClick={clearSheet}
							sx={{ textTransform: 'none', fontWeight: 600 }}
						>
							Clear
						</Button>
						<Button
							fullWidth
							variant="contained"
							onClick={applySheet}
							sx={{ textTransform: 'none', fontWeight: 600 }}
						>
							Show results
						</Button>
					</Stack>
				</Box>
			</Drawer>
		</>
	);
};

export default JobsMobileFilters;
